import { Link } from "react-router-dom";
import { useSettings } from "../contexts/SettingsContext";

// Static fallbacks — used only if the admin hasn't customised the About page yet.
// Mirrors backend DEFAULT_SETTINGS.about_page so preview + prod stay consistent.
const FALLBACK = {
  hero: {
    eyebrow: "The House of GOSHI",
    heading: "An atelier for the considered woman.",
    body: "Founded on the principle that less, made better, lasts longer — GOSHI designs womenswear with quiet authority. We work with heritage Italian mills, French ateliers and South Korean artisans, refining each piece across multiple seasons before release.",
    image_url: "https://images.pexels.com/photos/34921744/pexels-photo-34921744.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1400",
    video_url: "", video_webm: "", video_poster: "",
  },
  story: { enabled: false, eyebrow: "", heading: "", body: "", image_url: "" },
  founder: { enabled: false, name: "", title: "", quote: "", photo_url: "" },
  values: [
    { title: "Material", description: "Italian silk, Mongolian cashmere, French wool. Every fibre traced to source." },
    { title: "Craft", description: "Hand-finished seams. Bound buttonholes. Garments allowed the time they deserve." },
    { title: "Conscience", description: "Made in limited runs. Carbon-conscious shipping. Built to be loved long." },
  ],
  press_logos: [],
  closing: {
    eyebrow: "Our Promise",
    heading: "Fewer pieces. Greater intention. Worn always.",
    cta_label: "Discover the Collection",
    cta_link: "/shop",
  },
};

export default function About() {
  const { settings } = useSettings();
  const a = { ...FALLBACK, ...(settings.about_page || {}) };
  // Merge each subsection so any missing key falls back gracefully
  const hero = { ...FALLBACK.hero, ...(a.hero || {}) };
  const story = { ...FALLBACK.story, ...(a.story || {}) };
  const founder = { ...FALLBACK.founder, ...(a.founder || {}) };
  const closing = { ...FALLBACK.closing, ...(a.closing || {}) };
  const values = a.values && a.values.length ? a.values : FALLBACK.values;
  const pressLogos = a.press_logos || [];

  return (
    <div className="pt-32 pb-24">
      {/* HERO */}
      <section className="px-6 md:px-12 grid lg:grid-cols-12 gap-12 items-center" data-testid="about-hero">
        <div className="lg:col-span-7 space-y-6">
          {hero.eyebrow && <p className="text-xs uppercase tracking-[0.3em] text-gold">{hero.eyebrow}</p>}
          {hero.heading && <h1 className="font-serif text-5xl md:text-7xl leading-[1]">{hero.heading}</h1>}
          {(hero.eyebrow || hero.heading) && <div className="divider-gold" />}
          {hero.body && <p className="text-muted text-lg leading-relaxed max-w-xl whitespace-pre-line">{hero.body}</p>}
        </div>
        <div className="lg:col-span-5">
          {hero.video_url ? (
            <video
              className="w-full h-[600px] object-cover"
              autoPlay muted loop playsInline
              poster={hero.video_poster || hero.image_url || undefined}
              data-testid="about-hero-video"
            >
              {hero.video_webm && <source src={hero.video_webm} type="video/webm" />}
              <source src={hero.video_url} type="video/mp4" />
            </video>
          ) : hero.image_url ? (
            <img src={hero.image_url} alt={hero.heading || "GOSHI atelier"} className="w-full h-[600px] object-cover" data-testid="about-hero-image" />
          ) : null}
        </div>
      </section>

      {/* OUR STORY */}
      {story.enabled && (story.heading || story.body) && (
        <section className="px-6 md:px-12 py-24 grid lg:grid-cols-12 gap-12 items-center" data-testid="about-story">
          {story.image_url && (
            <div className="lg:col-span-5 order-2 lg:order-1">
              <img src={story.image_url} alt={story.heading || "Our story"} className="w-full h-[500px] object-cover" />
            </div>
          )}
          <div className={`space-y-6 ${story.image_url ? "lg:col-span-7 order-1 lg:order-2" : "lg:col-span-12 max-w-3xl mx-auto text-center"}`}>
            {story.eyebrow && <p className="text-xs uppercase tracking-[0.3em] text-gold">{story.eyebrow}</p>}
            {story.heading && <h2 className="font-serif text-4xl md:text-5xl leading-tight">{story.heading}</h2>}
            <div className="divider-gold" />
            {story.body && <p className="text-muted text-lg leading-relaxed whitespace-pre-line">{story.body}</p>}
          </div>
        </section>
      )}

      {/* VALUE CARDS */}
      {values.length > 0 && (
        <section
          className={`px-6 md:px-12 py-24 grid gap-12 ${values.length === 1 ? "md:grid-cols-1 max-w-2xl mx-auto" : values.length === 2 ? "md:grid-cols-2" : "md:grid-cols-3"}`}
          data-testid="about-values"
        >
          {values.map((b, i) => (
            <div key={i} className="bg-[#141414] p-10 border border-[rgba(244,244,240,0.04)]" data-testid={`about-value-card-${i}`}>
              <h3 className="font-serif text-2xl mb-3">{b.title}</h3>
              <div className="divider-gold mb-5" />
              <p className="text-muted leading-relaxed whitespace-pre-line">{b.description}</p>
            </div>
          ))}
        </section>
      )}

      {/* FOUNDER QUOTE */}
      {founder.enabled && (founder.quote || founder.name) && (
        <section className="px-6 md:px-12 py-24" data-testid="about-founder">
          <div className="max-w-4xl mx-auto grid md:grid-cols-[220px_1fr] gap-10 items-center">
            {founder.photo_url && (
              <img src={founder.photo_url} alt={founder.name || "Founder"} className="w-[220px] h-[220px] object-cover rounded-full mx-auto md:mx-0" />
            )}
            <div className="space-y-5 text-center md:text-left">
              {founder.quote && (
                <blockquote className="font-serif text-2xl md:text-3xl leading-snug text-text/90 whitespace-pre-line">
                  &ldquo;{founder.quote}&rdquo;
                </blockquote>
              )}
              <div className="divider-gold mx-auto md:mx-0 !w-16" />
              {founder.name && <p className="text-sm uppercase tracking-[0.25em] text-gold">{founder.name}</p>}
              {founder.title && <p className="text-xs uppercase tracking-[0.2em] text-muted">{founder.title}</p>}
            </div>
          </div>
        </section>
      )}

      {/* PRESS LOGOS */}
      {pressLogos.length > 0 && (
        <section className="px-6 md:px-12 py-16 border-t border-[rgba(244,244,240,0.06)]" data-testid="about-press">
          <p className="text-xs uppercase tracking-[0.3em] text-gold text-center mb-10">As seen in</p>
          <div className="flex flex-wrap items-center justify-center gap-x-16 gap-y-8 max-w-5xl mx-auto opacity-80">
            {pressLogos.map((logo, i) => {
              const img = <img src={logo.image_url} alt={logo.name || "Press"} className="h-8 md:h-10 object-contain grayscale hover:grayscale-0 transition duration-500" />;
              return (
                <div key={i} data-testid={`about-press-${i}`}>
                  {logo.link ? (
                    <a href={logo.link} target="_blank" rel="noopener noreferrer">{img}</a>
                  ) : img}
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* CLOSING */}
      {(closing.heading || closing.eyebrow) && (
        <section className="px-6 md:px-12 py-24 text-center max-w-3xl mx-auto" data-testid="about-closing">
          {closing.eyebrow && <p className="text-xs uppercase tracking-[0.3em] text-gold mb-6">{closing.eyebrow}</p>}
          {closing.heading && <h2 className="font-serif text-4xl md:text-5xl leading-tight">{closing.heading}</h2>}
          {closing.cta_label && (
            <Link to={closing.cta_link || "/shop"} className="btn-gold mt-10 inline-block" data-testid="about-closing-cta">
              {closing.cta_label}
            </Link>
          )}
        </section>
      )}
    </div>
  );
}
